import { useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Pin, Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichEditor } from "@/components/RichEditor";
import { FAMILY_ROLES } from "@/lib/family";
import { REL_GROUPS, relKey, uniqTropes } from "@/lib/blocks";

const ib = "p-1.5 rounded-md text-slate-500 hover:text-slate-100 hover:bg-veil/[0.05] disabled:opacity-30 transition-colors";
const move = (arr, i, d) => { const a = [...arr]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; };
const sel = "h-9 rounded-md bg-canvas border border-line text-sm text-slate-200 px-2";

// Text input that links itself when it matches a page title from the list
export const RefInput = ({ value, onChange, options, placeholder, testId }) => {
  const id = `opts-${testId}`;
  return (
    <>
      <Input list={id} value={value?.text || ""} placeholder={placeholder} data-testid={testId} className="h-9 bg-canvas"
        onChange={(e) => { const p = options.find((o) => o.title.toLowerCase() === e.target.value.trim().toLowerCase()); onChange({ text: e.target.value, page_id: p?.id || null }); }} />
      <datalist id={id}>{options.map((o) => <option key={o.id} value={o.title} />)}</datalist>
    </>
  );
};

export const JourneyEditor = ({ b, set, pages }) => {
  const [at, setAt] = useState(0);
  const tabs = b.tabs;
  const i = Math.min(at, tabs.length - 1);
  const setTabs = (t) => set({ tabs: t });
  return (
    <div className="space-y-3" data-testid={`journey-editor-${b.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {tabs.map((t, j) => (
          <button key={j} type="button" onClick={() => setAt(j)} data-testid={`journey-edit-tab-${j}`}
            className={`text-sm rounded-full border px-3 py-1 transition-colors ${j === i ? "border-amber-500/50 bg-amber-500/10 text-amber-200" : "border-line text-slate-400"}`}>{t.name || "Untitled"}</button>
        ))}
        <button type="button" onClick={() => { setTabs([...tabs, { name: `Part ${tabs.length + 1}`, html: "" }]); setAt(tabs.length); }} className={ib} data-testid="journey-add-tab"><Plus className="w-4 h-4" /></button>
      </div>
      {tabs[i] && (
        <>
          <div className="flex items-center gap-1">
            <Input value={tabs[i].name} maxLength={80} onChange={(e) => setTabs(tabs.map((t, j) => (j === i ? { ...t, name: e.target.value } : t)))} className="h-9 bg-canvas" data-testid="journey-tab-name" />
            <button type="button" disabled={i === 0} onClick={() => { setTabs(move(tabs, i, -1)); setAt(i - 1); }} className={ib} data-testid="journey-tab-left"><ArrowLeft className="w-4 h-4" /></button>
            <button type="button" disabled={i === tabs.length - 1} onClick={() => { setTabs(move(tabs, i, 1)); setAt(i + 1); }} className={ib} data-testid="journey-tab-right"><ArrowRight className="w-4 h-4" /></button>
            <button type="button" onClick={() => { setTabs(tabs.filter((_, j) => j !== i)); setAt(Math.max(0, i - 1)); }} className={`${ib} hover:text-rose-300`} data-testid="journey-tab-delete"><Trash2 className="w-4 h-4" /></button>
          </div>
          <RichEditor compact testId="journey-tab-editor" key={`${i}-${tabs.length}`} value={tabs[i].html} pages={pages} onChange={(html) => setTabs(tabs.map((t, j) => (j === i ? { ...t, html } : t)))} />
        </>
      )}
    </div>
  );
};

export const TimelineEditor = ({ b, set, pages }) => {
  const events = pages.filter((p) => p.category === "Events");
  const pts = b.points;
  const edit = (i, patch) => set({ points: pts.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  return (
    <div className="space-y-3" data-testid={`timeline-editor-${b.id}`}>
      {pts.map((p, i) => (
        <div key={i} className="rounded-lg border border-line p-3 space-y-2 border-l-2 border-l-amber-500/60" data-testid={`timeline-point-edit-${i}`}>
          <div className="flex gap-2">
            <Input value={p.date} maxLength={120} onChange={(e) => edit(i, { date: e.target.value })} placeholder="Date, e.g. Year 312" className="h-9 w-40 bg-canvas" data-testid={`tp-date-${i}`} />
            <Input value={p.title} maxLength={200} onChange={(e) => edit(i, { title: e.target.value })} placeholder="What happened" className="h-9 bg-canvas" data-testid={`tp-title-${i}`} />
            <button type="button" disabled={i === 0} onClick={() => set({ points: move(pts, i, -1) })} className={ib} data-testid={`tp-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
            <button type="button" disabled={i === pts.length - 1} onClick={() => set({ points: move(pts, i, 1) })} className={ib} data-testid={`tp-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
            <button type="button" onClick={() => set({ points: pts.filter((_, j) => j !== i) })} className={`${ib} hover:text-rose-300`} data-testid={`tp-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <Textarea rows={2} value={p.description} maxLength={2000} onChange={(e) => edit(i, { description: e.target.value })} placeholder="Short description" className="bg-canvas text-sm" data-testid={`tp-desc-${i}`} />
          <select value={p.event_id || ""} onChange={(e) => edit(i, { event_id: e.target.value || null })} className={`${sel} w-full`} data-testid={`tp-event-${i}`}>
            <option value="">No linked Event</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
        </div>
      ))}
      <button type="button" onClick={() => set({ points: [...pts, { date: "", title: "", description: "", event_id: null }] })} className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="tp-add"><Plus className="w-4 h-4" /> Add point</button>
    </div>
  );
};

// Entries live in the infobox Relationships facts; write-ups live on the block
export const RelationshipsEditor = ({ b, set, pages, sections, setSections }) => {
  const chars = pages.filter((p) => p.category === "Characters");
  const [draft, setDraft] = useState({ group: "Enemies", ref: { text: "", page_id: null }, role: "" });
  const facts = sections.find((s) => s.title === "Relationships")?.facts || [];
  const setItems = (group, fn) => setSections(sections.map((s) => (s.title !== "Relationships" ? s : {
    ...s, facts: s.facts.some((f) => f.key === group) ? s.facts.map((f) => (f.key === group ? { ...f, items: fn(f.items) } : f)) : [...s.facts, { key: group, items: fn([]) }],
  })));
  const add = () => {
    if (!draft.ref.text.trim()) return;
    setItems(draft.group, (items) => [...items.filter((x) => x.text || x.page_id), { text: draft.ref.text.trim(), page_id: draft.ref.page_id, note: "", role: draft.group === "Family" ? draft.role || null : null }]);
    setDraft({ ...draft, ref: { text: "", page_id: null } });
  };
  const note = (group, key) => b.notes.find((n) => n.group === group && n.key === key)?.html || "";
  const setNote = (group, key, html) => set({ notes: [...b.notes.filter((n) => !(n.group === group && n.key === key)), { group, key, html }] });
  return (
    <div className="space-y-4" data-testid={`relationships-editor-${b.id}`}>
      {REL_GROUPS.map((g) => (facts.find((f) => f.key === g)?.items || []).filter((x) => x.text || x.page_id).map((it, i) => (
        <div key={`${g}-${relKey(it)}`} className="rounded-lg border border-line p-3 space-y-2" data-testid={`rel-edit-${g}-${i}`}>
          <div className="flex items-center gap-2">
            <span className="font-code text-[10px] uppercase tracking-wider text-slate-500 w-20">{g}</span>
            <span className="flex-1 text-slate-100 truncate">{it.text}{it.role && <span className="text-xs text-slate-500"> · {FAMILY_ROLES.find((r) => r[0] === it.role)?.[1]}</span>}</span>
            <button type="button" onClick={() => { setItems(g, (items) => items.filter((x) => relKey(x) !== relKey(it))); set({ notes: b.notes.filter((n) => !(n.group === g && n.key === relKey(it))) }); }} className={`${ib} hover:text-rose-300`} data-testid={`rel-remove-${g}-${i}`}><X className="w-4 h-4" /></button>
          </div>
          <RichEditor compact testId={`rel-note-${g}-${i}`} value={note(g, relKey(it))} pages={pages} onChange={(html) => setNote(g, relKey(it), html)} />
        </div>
      )))}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-line p-3">
        <select value={draft.group} onChange={(e) => setDraft({ ...draft, group: e.target.value })} className={sel} data-testid="rel-add-group">{REL_GROUPS.map((g) => <option key={g}>{g}</option>)}</select>
        {draft.group === "Family" && (
          <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className={sel} data-testid="rel-add-role">
            <option value="">Role…</option>{FAMILY_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        <div className="flex-1 min-w-[160px]"><RefInput value={draft.ref} onChange={(ref) => setDraft({ ...draft, ref })} options={chars} placeholder="Character name" testId="rel-add-name" /></div>
        <button type="button" onClick={add} className="h-9 px-4 rounded-full bg-amber-500/15 text-amber-200 text-sm hover:bg-amber-500/25" data-testid="rel-add-btn">Add</button>
      </div>
    </div>
  );
};

export const TropesEditor = ({ b, set }) => {
  const [v, setV] = useState("");
  const add = () => { const t = v.trim(); if (t) set({ tropes: uniqTropes([...b.tropes, t]) }); setV(""); };
  return (
    <div className="space-y-2" data-testid={`tropes-editor-${b.id}`}>
      <div className="flex flex-wrap gap-1.5">
        {b.tropes.map((t) => <span key={t} className="inline-flex items-center gap-1 text-sm rounded-full border border-amber-500/30 px-3 py-0.5 text-amber-100">{t}<button type="button" onClick={() => set({ tropes: b.tropes.filter((x) => x !== t) })} data-testid={`trope-remove-${t}`}><X className="w-3 h-3" /></button></span>)}
      </div>
      <Input value={v} maxLength={60} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add a trope and press Enter" className="h-9 bg-canvas" data-testid="trope-input" />
    </div>
  );
};

export const QuoteForm = ({ q, onChange, chars, testId }) => {
  const lines = q.lines.length ? q.lines : [{ text: "", speaker: { text: "", page_id: null } }];
  const setLine = (i, patch) => onChange({ ...q, lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  return (
    <div className="space-y-2" data-testid={testId}>
      {lines.map((l, i) => (
        <div key={i} className="flex gap-2">
          <div className="w-40 shrink-0"><RefInput value={l.speaker} onChange={(speaker) => setLine(i, { speaker })} options={chars} placeholder="Speaker" testId={`${testId}-speaker-${i}`} /></div>
          <Input value={l.text} maxLength={2000} onChange={(e) => setLine(i, { text: e.target.value })} placeholder="Line" className="h-9 bg-canvas" data-testid={`${testId}-line-${i}`} />
          {lines.length > 1 && <button type="button" onClick={() => onChange({ ...q, lines: lines.filter((_, j) => j !== i) })} className={ib}><X className="w-4 h-4" /></button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onChange({ ...q, lines: [...lines, { text: "", speaker: { text: "", page_id: null } }] })} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid={`${testId}-add-line`}><Plus className="w-3 h-3" /> Line</button>
        <span className="text-xs text-slate-500 ml-auto">Said to</span>
        <div className="w-44"><RefInput value={q.said_to} onChange={(said_to) => onChange({ ...q, said_to })} options={chars} placeholder="Optional" testId={`${testId}-said-to`} /></div>
      </div>
    </div>
  );
};

export const QuotesEditor = ({ b, set, pages, onPin }) => {
  const chars = pages.filter((p) => p.category === "Characters");
  const blank = { said_to: { text: "", page_id: null }, lines: [{ text: "", speaker: { text: "", page_id: null } }] };
  return (
    <div className="space-y-3" data-testid={`quotes-editor-${b.id}`}>
      {b.quotes.map((q, i) => (
        <div key={i} className="rounded-lg border border-line p-3 space-y-2">
          <QuoteForm q={q} chars={chars} onChange={(nq) => set({ quotes: b.quotes.map((x, j) => (j === i ? nq : x)) })} testId={`quote-edit-${i}`} />
          <div className="flex justify-end gap-1">
            <button type="button" onClick={() => onPin(structuredClone(q))} className="text-xs flex items-center gap-1 text-slate-400 hover:text-amber-300 px-2 py-1" data-testid={`quote-pin-${i}`}><Pin className="w-3 h-3" /> Pin</button>
            <button type="button" onClick={() => set({ quotes: b.quotes.filter((_, j) => j !== i) })} className="text-xs flex items-center gap-1 text-slate-400 hover:text-rose-300 px-2 py-1" data-testid={`quote-delete-${i}`}><Trash2 className="w-3 h-3" /> Delete</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => set({ quotes: [...b.quotes, blank] })} className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="quote-add"><Plus className="w-4 h-4" /> Add quote</button>
    </div>
  );
};
